import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Shield, Users, Zap, CheckCircle, Globe, Clock, Heart } from 'lucide-react';
import imagen1 from '../assets/images/AboutPage/aboutpageimg1.webp';
import imagen2 from '../assets/images/AboutPage/aboutpageimg2.webp';
import imagen3 from '../assets/images/AboutPage/aboutpageimg3.webp';
import imagenwidget1 from '../assets/images/AboutPage/imagenwidget1.webp';
import imagenwidget2 from '../assets/images/AboutPage/imagenwidget2.webp';
import imagenwidget3 from '../assets/images/AboutPage/imagenwidget3.webp';
import imagenwidget4 from '../assets/images/AboutPage/imagenwidget4.webp';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const AnimatedCounter = ({ end, duration = 2, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (inView && !hasAnimated) {
      setHasAnimated(true);
      let startTime;
      
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(easeOutCubic * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [inView, end, duration, hasAnimated]);

  return (
    <motion.span
      ref={ref}
      className="text-3xl font-bold text-blue-700"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={inView ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {count}{suffix}
    </motion.span>
  );
};

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Seguridad Primero",
      description: "Tu seguridad es nuestra prioridad absoluta en cada producto que ofrecemos.",
      gradient: "from-blue-500 to-blue-800",
      image: imagenwidget1
    },
    {
      icon: Award,
      title: "Calidad Certificada", 
      description: "Productos certificados que cumplen con las normas internacionales más exigentes.",
      gradient: "from-yellow-500 to-yellow-800",
      image: imagenwidget2
    },
    {
      icon: Users,
      title: "Servicio Personalizado",
      description: "Atención especializada y asesoramiento técnico para cada cliente.",
      gradient: "from-blue-800 to-blue-500",
      image: imagenwidget3
    },
    {
      icon: Zap,
      title: "Innovación Constante",
      description: "Siempre a la vanguardia de las mejores tecnologías en seguridad eléctrica.",
      gradient: "from-yellow-800 to-yellow-500",
      image: imagenwidget4
    } 
  ];

  const achievements = [
    { number: 8, label: "Años de Experiencia", icon: Clock, suffix: "+" },
    { number: 500, label: "Clientes Satisfechos", icon: Heart, suffix: "+" },
    { number: 7500, label: "Proyectos Completados", icon: CheckCircle, suffix: "+" },
    { number: 5, label: "Países Atendidos", icon: Globe, suffix: "" }
  ];

  const images = [imagen1, imagen2, imagen3];
  const [currentIndex, setCurrentIndex] = useState(0);
  const particles = Array.from({ length: 25 });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 9000); // 9 segundos

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative min-h-screen py-12 overflow-hidden">
      {/* SLIFER FADE */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0">
          {images.map((img, index) => {
            const isActive = index === currentIndex;

            return (
              <motion.div
                key={index}
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${img})` }}
                initial={{ opacity: 0, scale: 1.02, filter: "blur(8px)" }}
                animate={
                  isActive
                    ? {
                      opacity: 1,
                      scale: 1,
                      filter: "blur(0px)"
                    }
                    : {
                      opacity: 0,
                      scale: 1.05,
                      filter: "blur(6px)"
                    }
                }
                transition={{
                  duration: 2.5,
                  ease: [0.4, 0, 0.2, 1] // easing crossfade cinematográfico
                }}
              />
            );
          })}
        </div>

        {/* OVERLAY GRADIENTE (SUAVIZADO ARRIBA → ABAJO) */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-black/70 to-black z-10" />

        {/* PARTICULAS SUAVES */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {particles.map((_, i) => {
            const size = Math.random() * 10 + 8;
            const left = Math.random() * 100;
            const duration = Math.random() * 5 + 5;
            const delay = Math.random() * 10;

            return (
              <motion.div
                key={i}
                className="absolute rounded-full bg-blue-300/80 blur-sm shadow-lg shadow-blue-400/60"
                style={{
                  width: size,
                  height: size,
                  left: `${left}%`,
                  bottom: "-100px"
                }}
                initial={{ y: 0 }}
                animate={{ y: -1200 }} // valor grande fijo
                transition={{
                  duration: duration,
                  repeat: Infinity,
                  ease: "linear",
                  delay: delay
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="container mx-auto px-4">
        
        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-gradient-to-br from-blue-400 to-blue-900 p-8 rounded-2xl shadow-xl shadow-gray-700 text-white">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-4 h-16 bg-white rounded flex items-center justify-center"></div>
                  <div> 
                    <h3 className="text-2xl font-bold">¿Que es Inverdron JGW?</h3>
                    <p className="text-white italic">Cuidamos a quienes dan energía al mundo</p>
                  </div>
                </div>
                <p className="text-white leading-relaxed text-justify">
                  Somos una empresa especializada en la comercialización y distribución de elementos 
                  para trabajos con tensión y EPPs para prácticas eléctricas seguras. Con más de 8 años 
                  de experiencia, nos hemos consolidado como líderes en el sector de seguridad eléctrica.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="bg-gradient-to-br from-gray-50  0 to-gray-400 p-6 rounded-xl border border-white shadow-xl shadow-gray-700">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Nuestra Misión</h4>
                <p className="text-gray-700 text-justify">
                  Proveer soluciones especializadas para trabajos con tensión y operaciones en sistemas eléctricos,
                  integrando equipos, dispositivos y elementos de protección certificados que garanticen seguridad operativa,
                  confiabilidad técnica y cumplimiento normativo. Respaldamos a quienes dan energía al mundo mediante tecnología,
                  conocimiento y soporte técnico orientados a la continuidad del servicio y la protección integral de la vida.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-400 p-6 rounded-xl border border-white shadow-xl shadow-gray-700">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Nuestra Visión</h4>
                <p className="text-gray-700 text-justify">
                  Para el año 2030, consolidarnos como referente nacional en distribución de elementos y equipos como soluciones técnicas
                  para trabajos con tensión y sistemas energizados, siendo aliado estratégico del sector eléctrico e industrial. Ser reconocidos
                  por nuestra excelencia técnica, rigor normativo, confiabilidad operativa e innovación, sustentados en una cultura organizacional
                  ética, competitiva y orientada al desarrollo permanente del talento humano.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mb-16">
          <motion.h2
            className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-white to-white bg-clip-text text-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Nuestros Valores
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="relative p-6 rounded-xl shadow-xl shadow-gray-700 hover:shadow-2xl transition-all overflow-hidden"
                style={{
                  backgroundImage: `url(${value.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 8, y: 0 }}
                transition={{ delay: 0 + index * 0.1 }}
                whileHover={{ y: -20 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-white/40"></div>

                <div className="relative z-10">
                  <div className={`w-12 h-12 bg-gradient-to-r ${value.gradient} rounded-lg flex items-center justify-center mb-4`}>
                    <value.icon className="w-6 h-6 text-white" />
                  </div>

                  <h4 className="text-lg font-bold text-white mb-2">
                    {value.title}
                  </h4>

                  <p className="text-white font-semibold text-sm">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-transparent to-transparent p-12 rounded-2xl">
            <motion.h2
              className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-white to-white bg-clip-text text-transparent"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Nuestros Logros
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className="text-center bg-white p-6 rounded-xl shadow-xl shadow-gray-700 hove:shadow-2xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0 + index * 0.1 }}
                  whileHover={{ scale: 1.1 }}
                >
                  <div className="bg-gradient-to-r from-blue-400 to-blue-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <achievement.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="mb-2">
                    <AnimatedCounter 
                      end={achievement.number} 
                      duration={6} 
                      suffix={achievement.suffix} 
                    />
                  </div>
                  <p className="text-gray-800 font-semibold">{achievement.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutPage;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.